// Shared language registry (frontend mirror of backend app/services/languages.py).
//
// SOURCE_LOCALE is authored by hand. A locale is *enabled* only once its full
// catalog has been reviewed — enabling a half-translated interface is forbidden
// (see platform-localization spec §6). FR/ES move into ENABLED_LOCALES in Phase 2
// when their catalogs are complete and reviewed.
//
// `qps` is a pseudo-locale used only by the hardcoded-string sweep. It is never
// shipped and never appears in the production picker.

import statuses from '../../lang/_status.json'

export const SOURCE_LOCALE = 'en'

// What an anonymous visitor sees before choosing anything. sondely.com is
// written for Montreal's community sector, where the working language is
// French, so French is the default for everyone rather than only for French
// browsers (Luca, 2026-09-21). SOURCE_LOCALE stays 'en': English remains the
// language the catalogs are authored against and the fallback for a missing
// key, which is a separate question from what a visitor lands on.
export const DEFAULT_LOCALE = 'fr'

// Enablement is DATA, not code: a locale is enabled when its catalog's entry in
// frontend/lang/_status.json is APPROVED — the reviewer's sign-off is the act
// that ships the language (add-a-language E2E proves zero code edits). The
// backend derives its list from the same file (app/services/languages.py).
export const ENABLED_LOCALES: readonly string[] = [
  SOURCE_LOCALE,
  ...Object.entries(statuses as Record<string, string>)
    .filter(([, status]) => status === 'APPROVED')
    .map(([locale]) => locale),
]

export const PSEUDO_LOCALE = 'qps'

export type Locale = string

// Display names for the picker. Native name first (what a speaker recognises).
// Locales beyond this map resolve through Intl.DisplayNames in their own
// language, so a newly enabled catalog needs no name entry here.
export const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  qps: 'Pseudo (qps)',
}

export function languageName(code: string): string {
  if (LANGUAGE_NAMES[code]) return LANGUAGE_NAMES[code]
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code)
    if (name && name !== code) return name.charAt(0).toLocaleUpperCase(code) + name.slice(1)
  } catch {
    // Unknown/invalid code — fall through to the raw code.
  }
  return code
}

// Locales offered in the picker. In dev we also expose the pseudo-locale so the
// hardcoded-string sweep can be driven from the UI; never in production.
export function pickerLocales(): string[] {
  const base = [...ENABLED_LOCALES]
  if (import.meta.env.DEV) return [...base, PSEUDO_LOCALE]
  return base
}

// Narrow a raw navigator string (e.g. "fr-CA") to an enabled base locale, else null.
export function narrowToEnabled(raw: string | undefined | null): string | null {
  if (!raw) return null
  const base = raw.toLowerCase().split('-')[0]
  return (ENABLED_LOCALES as readonly string[]).includes(base) ? base : null
}
